<ng-container *transloco="let t; read: 'operations.profileWizard'">
  <!-- PRESETS -->
  <div class="mb-6 flex items-center gap-2">
    @for (preset of presets; track preset.key) {
      <button
        type="button"
        (click)="applyPreset(preset)"
        class="rounded-control px-3 py-1.5 text-[13px] font-semibold"
        [class.bg-action]="isPresetActive(preset)"
        [class.text-action-text]="isPresetActive(preset)"
        [class.border]="!isPresetActive(preset)"
        [class.border-hairline]="!isPresetActive(preset)"
        [class.bg-card]="!isPresetActive(preset)"
        [class.text-ink-1]="!isPresetActive(preset)"
        [class.hover:bg-sunken]="!isPresetActive(preset)"
      >
        {{ t('editor.presets.' + preset.key) }}
      </button>
    }
  </div>

  <!-- TRACK -->
  <div class="px-2 pt-9">
    <div #track class="te-track">
      <!-- The shared threshold bar draws the zone/hysteresis segments; the
           editor only overlays its drag markers on top. -->
      <app-power-threshold-bar
        [shutdownPct]="value('npEnter')"
        [shutdownOutPct]="value('npExit')"
        [lowPct]="value('lpEnter')"
        [lowOutPct]="value('lpExit')"
        [height]="20"
        [labels]="false"
      />
      <span class="te-scale-label" style="left: 0">0%</span>
      <span class="te-scale-label" style="right: 0">100%</span>

      @for (m of markers; track m.key) {
        <div class="te-marker" [style.left.%]="value(m.key)">
          <div class="te-marker-line"></div>
          <div
            tabindex="0"
            role="slider"
            [attr.aria-label]="t('editor.aria.' + m.key)"
            [attr.aria-valuenow]="value(m.key)"
            aria-valuemin="0"
            aria-valuemax="100"
            [attr.title]="t('editor.titles.' + m.key)"
            (pointerdown)="startDrag(m.key, $event)"
            (keydown)="nudge(m.key, $event)"
            class="te-chip"
            [class.te-chip-above]="m.above"
            [class.te-chip-below]="!m.above"
            [class.te-chip-active]="dragKey() === m.key"
          >
            <span [class.te-arrow-red]="m.tone === 'red'" [class.te-arrow-orange]="m.tone === 'orange'">
              {{ m.enter ? '↓' : '↑' }}
            </span>
            {{ value(m.key) }}%
          </div>
        </div>
      }
    </div>

    <!-- LEGEND -->
    <div class="mt-11 flex items-center gap-5 text-[12px] text-ink-2">
      <span class="flex items-center gap-1.5"><span class="te-swatch te-seg-no-power"></span>{{ t('editor.legend.noPower') }}</span>
      <span class="flex items-center gap-1.5"><span class="te-swatch te-seg-low-power"></span>{{ t('editor.legend.lowPower') }}</span>
      <span class="flex items-center gap-1.5"><span class="te-swatch te-seg-normal"></span>{{ t('editor.legend.normal') }}</span>
      <span class="flex items-center gap-1.5"><span class="te-swatch te-hyst-no"></span>{{ t('editor.legend.hysteresis') }}</span>
    </div>
    <div class="mt-2 text-[12px] text-ink-3">{{ t('editor.hysteresisNote') }}</div>
  </div>

  <!-- VALUE CARDS -->
  <div class="mt-6 grid gap-4" style="grid-template-columns: repeat(auto-fit, minmax(240px, 1fr))">
    @for (card of cards; track card.kicker) {
      <div class="flex flex-col gap-3 rounded-control p-4 shadow-hairline">
        <div class="flex items-center gap-2 font-display text-[13px] font-semibold tracking-wide text-ink-2 uppercase">
          <span
            class="h-2 w-2 rounded-pill"
            [class.te-dot-orange]="card.tone === 'orange'"
            [class.te-dot-red]="card.tone === 'red'"
          ></span>
          {{ t('fields.' + card.kicker) }}
        </div>
        <div class="flex items-center gap-3">
          @for (key of card.fields; track key; let first = $first) {
            <div class="te-field" [class.te-field-focused]="focusedKey() === key">
              <div class="flex min-w-0 flex-1 flex-col justify-center">
                <span class="text-[11px] whitespace-nowrap text-ink-3">
                  {{ first ? t('fields.enterBelow') : t('fields.exitAbove') }}
                </span>
                <div class="flex items-baseline gap-0.5">
                  <input
                    #field
                    [value]="fieldText(key)"
                    inputmode="numeric"
                    [attr.aria-label]="t('editor.aria.' + key)"
                    (focus)="onFieldFocus(key, field)"
                    (input)="onFieldInput(field)"
                    (blur)="commitField(key)"
                    (keydown)="onFieldKeydown(key, $event)"
                    class="te-field-input"
                  />
                  <span class="text-[12px] text-ink-3">%</span>
                </div>
              </div>
              <div class="flex flex-col gap-0.5">
                <button
                  type="button"
                  [attr.aria-label]="t('editor.aria.increase')"
                  (click)="stepField(key, 1)"
                  class="te-step-btn"
                >
                  <span class="te-tri-up"></span>
                </button>
                <button
                  type="button"
                  [attr.aria-label]="t('editor.aria.decrease')"
                  (click)="stepField(key, -1)"
                  class="te-step-btn"
                >
                  <span class="te-tri-down"></span>
                </button>
              </div>
            </div>
          }
        </div>
      </div>
    }
  </div>
</ng-container>
